import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ASSETS, BRAND } from "@/lib/brand";
import { Reveal } from "@/components/Motion";
import { CheckCircle2, Building2, Percent, Globe2, Truck, ArrowRight, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  { icon: Percent, t: "Trade pricing", d: "Access wholesale rates on corals and fish, built for resale margins." },
  { icon: Globe2, t: "Direct imports", d: "First pick of shipments imported directly from Australia & Indonesia." },
  { icon: Truck, t: "Priority fulfilment", d: "Bulk orders, priority allocation and coordinated livestock logistics." },
  { icon: Building2, t: "Dedicated support", d: "A direct line for restocks, availability lists and special requests." },
];

const EMPTY = { business_name: "", contact_name: "", email: "", phone: "", business_type: "", resale_id: "", website: "", message: "" };

export default function Wholesale() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.email.trim()) return toast.error("Business name and email are required");
    setBusy(true);
    try {
      await api.post("/wholesale/apply", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden flex items-end">
        <img src={ASSETS.banner} alt="Exotic Reefing wholesale" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-void via-void/50 to-void/10" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pb-14 w-full">
          <Reveal>
            <span className="mono-label text-cyan">For business owners & retailers</span>
            <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-4 max-w-3xl leading-[0.95]">Wholesale <span className="italic text-cyan">program</span></h1>
            <p className="text-white/70 mt-5 max-w-xl">Buy exotic corals & fish at trade pricing. We import directly from <b className="text-white">Australia</b> and <b className="text-white">Indonesia</b> — apply below for a wholesale account.</p>
          </Reveal>
        </div>
      </section>

      {/* PERKS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PERKS.map((p, i) => (
            <motion.div key={p.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.07 }}
              className="glass rounded-2xl p-6 border border-white/10 card-glow">
              <div className="w-11 h-11 rounded-xl bg-cyan/10 border border-cyan/20 flex items-center justify-center mb-4">
                <p.icon className="text-cyan" size={20} />
              </div>
              <h3 className="font-display text-xl mb-2">{p.t}</h3>
              <p className="text-white/50 text-sm">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FORM */}
      <section className="max-w-[900px] mx-auto px-5 md:px-10 pb-28">
        <div className="glass rounded-3xl p-8 md:p-12 border border-cyan/20" style={{ boxShadow: "0 30px 90px -40px rgba(0,240,255,0.3)" }} data-testid="wholesale-form-card">
          {done ? (
            <div className="text-center py-10" data-testid="wholesale-success">
              <PartyPopper className="mx-auto text-cyan mb-5" size={48} />
              <h2 className="font-display text-4xl mb-3">Application received</h2>
              <p className="text-white/60 max-w-md mx-auto">Thanks! We've got your wholesale application and will review it shortly. We'll reach out to <b className="text-white">{form.email}</b> with your trade access and pricing.</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <span className="mono-label text-cyan">Apply now</span>
              <h2 className="font-display text-4xl mt-3 mb-8">Wholesale application</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <F label="Business name *"><input className="er-input" data-testid="ws-business" value={form.business_name} onChange={set("business_name")} /></F>
                <F label="Contact name"><input className="er-input" data-testid="ws-contact" value={form.contact_name} onChange={set("contact_name")} /></F>
                <F label="Email *"><input type="email" className="er-input" data-testid="ws-email" value={form.email} onChange={set("email")} /></F>
                <F label="Phone"><input className="er-input" data-testid="ws-phone" value={form.phone} onChange={set("phone")} /></F>
                <F label="Business type"><input className="er-input" placeholder="Fish store, aquascaper, online reseller…" value={form.business_type} onChange={set("business_type")} /></F>
                <F label="Resale / Tax ID"><input className="er-input" value={form.resale_id} onChange={set("resale_id")} /></F>
                <div className="sm:col-span-2"><F label="Website / social"><input className="er-input" value={form.website} onChange={set("website")} /></F></div>
                <div className="sm:col-span-2"><F label="Tell us about your business"><textarea rows={3} className="er-input" value={form.message} onChange={set("message")} /></F></div>
              </div>
              <button disabled={busy} data-testid="ws-submit" className="btn-pill btn-solid w-full mt-7 flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? "Submitting…" : <>Submit application <ArrowRight size={15} /></>}
              </button>
              <p className="text-white/30 text-xs mt-4 text-center font-mono">We'll review and email your wholesale access. Questions? Call {BRAND.phone}.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function F({ label, children }) {
  return (<div><label className="mono-label text-white/40 block mb-2">{label}</label>{children}</div>);
}
